import { Injectable } from '@angular/core';
import { type Faro, getWebInstrumentations, initializeFaro } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import { environment } from '../../../../environments/environment';

/**
 * Service for initializing and managing Grafana Faro frontend observability.
 * Provides end-to-end visibility for user interactions, errors, and HTTP requests.
 */
@Injectable({
  providedIn: 'root',
})
export class FaroService {
  #faro: Faro | null = null;

  /**
   * Initializes Grafana Faro with web instrumentations and tracing.
   * Should be called once during application bootstrap.
   * Skips initialization if Faro URL is not configured.
   */
  initialize(): void {
    // Skip initialization if Faro URL is not configured
    if (!environment.faro?.url || environment.faro.url.trim() === '') {
      console.warn('Grafana Faro: Collector URL not configured, skipping initialization');
      return;
    }

    try {
      this.#faro = initializeFaro({
        url: environment.faro.url,
        app: {
          name: environment.faro.appName,
          version: '1.0.0', // TODO: Consider dynamically injecting from package.json
          environment: environment.faro.environment,
        },
        instrumentations: [
          // Mandatory - provides default web instrumentations (console, errors, web vitals)
          ...getWebInstrumentations(),
          // Tracing package for end-to-end visibility of HTTP requests
          new TracingInstrumentation(),
        ],
      });

      console.log('Grafana Faro initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Grafana Faro:', error);
    }
  }

  /**
   * Gets the Faro instance if initialized.
   * Use this to access Faro API for custom events, logs, or measurements.
   */
  getInstance(): Faro | null {
    return this.#faro;
  }
}
